import React, {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Toast} from 'antd-mobile';
import {hideLoading, showLoading} from '@/components/loading';
import {getUrlAllParams} from '@/utils/publicFun';
import Service from '../service';
import {MOCK_SYNTHESIZED_LIST, TYPE_TABS} from './mock';
import Styles from './index.module.scss';

const USE_MOCK = Service.mock;
const PAGE_SIZE = 50;

const tabLabelMap = TYPE_TABS.reduce((map, tab) => {
  map[String(tab.cate)] = tab.label;
  return map;
}, {});

const getInitialTab = (resultType) => {
  const cate = Number(resultType);
  if (TYPE_TABS.some((tab) => tab.cate === cate)) return cate;
  return TYPE_TABS[0].cate;
};

/** 将接口 SynthesizeRecordResp 转为页面结构 */
const normalizeRecord = (item = {}) => {
  const resultType = String(item.resultType ?? '');
  const label = tabLabelMap[resultType] || '';
  return {
    ...item,
    id: item.id,
    collectionName: item.resultNo || (label ? `禁忌的色彩-${label}` : ''),
    nftImage: item.resultImg || '',
    roleType: Number(item.resultType) || 0,
    status: Number(item.status),
  };
};

/**
 * @description 已合成藏品（话剧）
 * @return {/culturalExchange_huaju/exchangeRecord}
 */
const ExchangeRecord = () => {
  const navigate = useNavigate();
  const {search} = useLocation();
  const {resultType: queryResultType} = getUrlAllParams(search.slice(1));
  const [list, setList] = useState([]);
  const [activeType, setActiveType] = useState(() => getInitialTab(queryResultType));
  const [statusBarHeight, setStatusBarHeight] = useState(0);

  const typeTabs = useMemo(() => TYPE_TABS, []);

  useEffect(() => {
    if (window.JsBridge?.hasWebViewBridge?.()) {
      window.JsBridge.setStatusBar?.('setBarStyle', ['dark-content']);
      window.JsBridge.getStatusBarHeight().then(({height}) => {
        setStatusBarHeight(height || 0);
      });
    }
  }, []);

  useEffect(() => {
    getData(activeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  const getData = async (resultType) => {
    if (USE_MOCK) {
      setList(
        MOCK_SYNTHESIZED_LIST
          .filter((item) => item.roleType === resultType)
          .map(normalizeRecord),
      );
      return;
    }
    try {
      showLoading();
      const res = await Service.querySynthesizeRecords({
        resultType: String(resultType),
        page: 1,
        size: PAGE_SIZE,
      });
      if (res.code !== '000000') throw new Error(res.msg || '加载失败');
      setList((res.data || []).map(normalizeRecord));
    } catch (error) {
      Toast.show(error?.message || '加载失败');
      setList([]);
    } finally {
      hideLoading();
    }
  };

  const handleBack = () => {
    if (window.JsBridge?.hasWebViewBridge?.()) {
      window.JsBridge.back();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={Styles.synthesizedPage}>
      <div className={Styles.pageHeader}>
        <div
          className={Styles.statusBar}
          style={statusBarHeight ? {height: statusBarHeight} : undefined}
        />
        <div className={Styles.navBar}>
          <div
            className={Styles.backButton}
            onClick={handleBack}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleBack()}
            aria-label="返回"
          />
          <div className={Styles.title}>已合成藏品</div>
          <div className={Styles.placeholder} />
        </div>
      </div>

      <div className={Styles.contentArea}>
        <div className={Styles.typeTabs}>
          {typeTabs.map((t) => (
            <button
              key={t.cate}
              type="button"
              className={`${Styles.typeTab} ${activeType === t.cate ? Styles.typeTabActive : Styles.typeTabInactive}`}
              onClick={() => setActiveType(t.cate)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className={Styles.gridScrollArea}>
          {list.length === 0 ? (
            <div className={Styles.noData}>
              <div className={Styles.noDataText}>您没有已合成的藏品</div>
            </div>
          ) : (
            <div className={Styles.grid}>
              {list.map((item) => (
                <div key={item.id} className={Styles.gridItem}>
                  <div className={Styles.itemImg}>
                    {item.nftImage ? (
                      <img src={item.nftImage} alt={item.collectionName} />
                    ) : null}
                  </div>
                  <div className={Styles.itemName}>{item.collectionName}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExchangeRecord;
